// Arrastar para reorganizar (0.34), em qualquer lista do app.
// Segurar o dedo sobre o item (toque longo) "levanta" o item com uma vibração leve; arrastando para cima ou para baixo,
// os outros abrem espaço; perto da borda a tela rola sozinha; soltando, a nova ordem é salva.
// Listas do mesmo "grupo" trocam itens entre si (ex.: item de uma caixa para outra, tarefa de um cômodo para outro).
// Feito só com o que já vem no React Native (PanResponder + Animated): funciona no app instalado, no iPhone e no site.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Animated, Keyboard, PanResponder, Platform, Pressable, ScrollView, Text, TextInput, View, type ScrollViewProps, type StyleProp, type ViewStyle } from 'react-native';
import * as Haptics from 'expo-haptics';
import Svg, { Circle } from 'react-native-svg';
import { C } from './theme';
import { ROLAGEM_TECLADO, useTeclado } from './teclado';
import { t } from '../lib/i18n';
import { useCaixas } from '../lib/caixas';
import { tocouEm } from '../lib/caixaAtiva';

type Rect = { x: number; y: number; w: number; h: number };
type Zona = { id: string; grupo: string; medir: () => Promise<Rect | null>; lista?: boolean };

// ---------- estado global do arrasto (só um por vez) ----------
let alvoZona: string | null = null;
let arrastando = false;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach(f => f());
const assinar = (f: () => void) => { ouvintes.add(f); return () => { ouvintes.delete(f); }; };
const setZona = (z: string | null) => { if (z !== alvoZona) { alvoZona = z; avisar(); } };
const setArrastando = (v: boolean) => {
  if (v === arrastando) return;
  arrastando = v; avisar();
  // no navegador, não selecionar texto enquanto arrasta
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    document.body.style.userSelect = v ? 'none' : '';
    if (v) window.getSelection?.()?.removeAllRanges();
  }
};
/** true enquanto algum item está sendo arrastado (para mostrar destinos, por exemplo). */
export const useArrastando = () => useSyncExternalStore(assinar, () => arrastando, () => false);
const useZonaAlvo = () => useSyncExternalStore(assinar, () => alvoZona, () => null);

const zonas = new Map<string, Zona>();   // chave única por registro
const vibrar = (forte = false) => {
  if (Platform.OS === 'web') return;
  (forte ? Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light) : Haptics.selectionAsync()).catch(() => {});
};
/** Texto 28 (REGRAS 0.4): a segunda vibração, quando o "segurar" vira seleção (diferente da do "pegar"). */
const vibrarSelecao = () => { if (Platform.OS !== 'web') Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {}); };
const medirView = (v: View | null): Promise<Rect | null> => new Promise(res => {
  if (!v) return res(null);
  try { v.measureInWindow((x, y, w, h) => res(w || h ? { x, y, w, h } : null)); } catch { res(null); }
});

// ---------- área com rolagem (rola sozinha perto da borda e mostra o item levantado por cima de tudo) ----------
type Area = {
  y: { current: number };
  medirViewport: () => Promise<Rect | null>;
  rolarPor: (dy: number) => number;
  setRolagem: (on: boolean) => void;
  fantasma: (no: ReactNode | null, r?: Rect) => void;
  mover: (dx: number, dy: number) => void;
  /** texto 29 (REGRAS 0.4): o anelzinho "continue segurando para selecionar" perto do dedo (ou null para tirar) */
  anel: (p: { x: number; y: number; ms: number } | null) => void;
};
const AreaCtx = createContext<Area | null>(null);
/** Área com rolagem em volta (ou null): o quadro das páginas usa para travar a rolagem da tela enquanto arrasta (texto 11c B11). */
export const useAreaRolagem = () => useContext(AreaCtx);

/** ScrollView que permite arrastar itens das listas de dentro dele. Use no lugar do ScrollView. */
export function ScrollArrastavel({ children, onScroll, style, rolagemRef, ...p }: ScrollViewProps & { children: ReactNode;
  /** texto 20 (2): quem precisa rolar a página por fora (ex.: "⬆ Topo") */
  rolagemRef?: { current: ScrollView | null } }) {
  const ref = useRef<ScrollView | null>(null);
  const raiz = useRef<View | null>(null);
  const y = useRef(0);
  const alturaConteudo = useRef(0);
  const alturaVisivel = useRef(0);
  const [rolagem, setRolagem] = useState(true);
  const [fant, setFant] = useState<{ no: ReactNode; r: Rect; base: Rect } | null>(null);
  const [anel, setAnel] = useState<{ x: number; y: number } | null>(null);
  const prog = useRef(new Animated.Value(0)).current;
  const pos = useRef(new Animated.ValueXY()).current;
  // texto 18 (A5/B3): com o teclado aberto a página rola até o fim, acima dele (iPhone: o espaço do teclado;
  // Android: um espaço do tamanho do teclado no fim da página, porque a tela não encolhe)
  const tec = useTeclado();
  const tecAlt = useRef(0); tecAlt.current = tec.alt;
  const area = useMemo<Area>(() => ({
    y,
    medirViewport: () => medirView(raiz.current),
    rolarPor: (dy: number) => {
      const max = Math.max(0, alturaConteudo.current - alturaVisivel.current + (Platform.OS === 'ios' ? tecAlt.current : 0));
      const novo = Math.max(0, Math.min(max, y.current + dy));
      const feito = novo - y.current;
      if (feito) { y.current = novo; ref.current?.scrollTo({ y: novo, animated: false }); }
      return feito;
    },
    setRolagem,
    fantasma: (no, r) => {
      if (!no || !r) { setFant(null); return; }
      pos.setValue({ x: 0, y: 0 });
      medirView(raiz.current).then(base => setFant({ no, r, base: base ?? { x: 0, y: 0, w: 0, h: 0 } }));
    },
    mover: (dx, dy) => pos.setValue({ x: dx, y: dy }),
    anel: p => {
      prog.stopAnimation();
      if (!p) { setAnel(null); return; }
      medirView(raiz.current).then(base => {
        setAnel({ x: p.x - (base?.x ?? 0), y: p.y - (base?.y ?? 0) });
        prog.setValue(0);
        Animated.timing(prog, { toValue: 1, duration: p.ms, useNativeDriver: false }).start();
      });
    },
  }), [pos, prog]);
  // Android (texto 18 B1): o sistema não leva o campo para cima do teclado; a página rola sozinha até ele
  useEffect(() => {
    if (Platform.OS !== 'android' || !tec.alt) return;
    const t0 = setTimeout(() => {
      const campo: any = (TextInput as any).State?.currentlyFocusedInput?.();
      if (!campo?.measureLayout || !raiz.current) return;
      campo.measureLayout(raiz.current as any, () => {
        campo.measureInWindow((_x: number, yy: number, _w: number, h: number) => {
          const limite = tec.topo - 16;
          if (yy + h > limite) area.rolarPor(yy + h - limite);
        });
      }, () => { /* o campo não é desta página */ });
    }, 80);
    return () => clearTimeout(t0);
  }, [tec.alt, tec.topo, area]);
  return (
    <AreaCtx.Provider value={area}>
      <View ref={raiz} style={[{ flex: 1 }, style as any]} collapsable={false} onStartShouldSetResponderCapture={() => { tocouEm(null); return false; }}>
        <ScrollView ref={no => { ref.current = no; if (rolagemRef) rolagemRef.current = no; }} {...ROLAGEM_TECLADO} {...p} style={{ flex: 1 }} scrollEnabled={rolagem && p.scrollEnabled !== false} scrollEventThrottle={16}
          onScroll={e => { y.current = e.nativeEvent.contentOffset.y; onScroll?.(e); }}
          onContentSizeChange={(w, h) => { alturaConteudo.current = h; p.onContentSizeChange?.(w, h); }}
          onLayout={e => { alturaVisivel.current = e.nativeEvent.layout.height; p.onLayout?.(e); }}>
          {children}
          {Platform.OS === 'android' && tec.alt > 0 && <View style={{ height: tec.alt }} />}
        </ScrollView>
        {anel && <Anel x={anel.x} y={anel.y} prog={prog} />}
        {fant && (
          <Animated.View pointerEvents="none" style={{ position: 'absolute', left: fant.r.x - fant.base.x, top: fant.r.y - fant.base.y, width: fant.r.w,
            transform: [{ translateX: pos.x }, { translateY: pos.y }, { scale: 1.03 }], zIndex: 999, elevation: 12,
            shadowColor: '#000', shadowOpacity: 0.35, shadowRadius: 12, shadowOffset: { width: 0, height: 6 }, opacity: 0.97 }}>
            {fant.no}
          </Animated.View>
        )}
      </View>
    </AreaCtx.Provider>
  );
}

const CirculoAnim = Animated.createAnimatedComponent(Circle);
/** Texto 29 (REGRAS 0.4): anelzinho enchendo acima do dedo, com "continue segurando para selecionar". */
function Anel({ x, y, prog }: { x: number; y: number; prog: Animated.Value }) {
  const R = 14, L = 2 * Math.PI * R;
  const off = prog.interpolate({ inputRange: [0, 1], outputRange: [L, 0] });
  return (
    <View pointerEvents="none" style={{ position: 'absolute', left: Math.max(8, x - 110), top: Math.max(4, y - 96), width: 220, alignItems: 'center', zIndex: 1000 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: C.card, borderRadius: 18, paddingVertical: 6, paddingHorizontal: 10,
        shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 8, shadowOffset: { width: 0, height: 3 }, elevation: 10 }}>
        <Svg width={34} height={34}>
          <Circle cx={17} cy={17} r={R} stroke={C.line} strokeWidth={4} fill="none" />
          <CirculoAnim cx={17} cy={17} r={R} stroke="#6366F1" strokeWidth={4} fill="none" strokeDasharray={`${L}`} strokeDashoffset={off as any}
            strokeLinecap="round" transform="rotate(-90 17 17)" />
        </Svg>
        <Text style={{ color: C.text, fontSize: 12, fontWeight: '700', flexShrink: 1 }}>{t('continue segurando para selecionar')}</Text>
      </View>
    </View>
  );
}

/** Área que recebe itens arrastados de listas do mesmo grupo (ex.: o cartão de um cômodo ou de um fichário fechado). */
export function ZonaSoltar({ grupo, id, children, style, corDestaque, toque }: { grupo: string; id: string; children: ReactNode; style?: StyleProp<ViewStyle>; corDestaque?: string;
  /** texto 26: os toques do cartão inteiro (segurar numa parte livre e arrastar) */ toque?: object }) {
  const ref = useRef<View | null>(null);
  const alvo = useZonaAlvo();
  useEffect(() => {
    const k = `z:${grupo}:${id}:${Math.random()}`;
    zonas.set(k, { id, grupo, medir: () => medirView(ref.current) });
    return () => { zonas.delete(k); };
  }, [grupo, id]);
  const on = alvo === `${grupo}:${id}`;
  return (
    <View ref={ref} collapsable={false} {...(toque ?? {})} style={[style, on ? { borderColor: corDestaque ?? C.ok, borderWidth: 2 } : null]}>
      {children}
    </View>
  );
}

type Props<T> = {
  itens: T[];
  chave: (x: T) => string;
  /** desenha o item; "alca" (com a prop alca) vai na View do item inteiro; os campos de texto dentro dele levam semArrastar (texto 26). */
  render: (x: T, i: number, info: { arrastando: boolean; alca: object; alvo: boolean }) => ReactNode;
  onOrdem: (novos: T[]) => void;
  /** listas do mesmo grupo trocam itens */
  grupo?: string;
  listaId?: string;
  onMover?: (x: T, destinoId: string, indice: number) => void;
  gap?: number;
  grade?: boolean;          // em grade (como os ícones da tela do celular)
  alca?: boolean;           // só a alça levanta o item
  /** texto 19 (H1): a alça é um controle só de arrastar: a rolagem trava no toque e o bloco levanta logo
   *  (sem esperar segurar parado); um toque curto na alça chama onToqueAlca (ex.: recolher o bloco). */
  alcaImediata?: boolean;
  onToqueAlca?: (k: string) => void;
  desligado?: boolean;
  style?: StyleProp<ViewStyle>;
  /** estilo da caixa de cada item (ex.: largura na grade) */
  estiloItem?: (x: T, i: number) => StyleProp<ViewStyle>;
  onArrastando?: (on: boolean) => void;
  /** 0.65: segurar e soltar sem mexer entra no modo de seleção (caixinhas em cada item).
   *  0.69 (B1): segurar parado ~0,5 s já entra na seleção (vibração leve), sem esperar soltar; se o dedo andar depois, vira arraste. */
  selecao?: { ativa: boolean; marcados: Set<string>; onIniciar: (k: string) => void; onAlternar: (k: string) => void; onCancelar?: () => void; cor?: string };
  /** texto 29: só estes entram na seleção (ex.: nos blocos de uma caixa, os de Texto livre e Desenho; subtítulos não) */
  selecionavel?: (x: T) => boolean;
};

// Texto 28 (A, REGRAS 0): TEMPO DO TOQUE no app inteiro.
// CAUSA de "rolar a tela pega e move itens e caixas": o item "pegava" com só 0,32 s de dedo parado (e a seleção com 0,5 s).
// Quem encosta o dedo, espera um instante e só então rola (ou encosta para parar uma rolagem) passava desse tempo: o item
// levantava, a rolagem travava e o resto do gesto virava arraste. Nos cabeçalhos dos blocos (e dos subtítulos) era pior:
// a rolagem travava no instante do toque e qualquer movimento de 6 px já levantava o bloco.
// Agora: passar o dedo só rola; toque (mesmo demorado ou com o dedo mexendo alguns milímetros) edita/abre;
// segurar ~1 s (v3) pega para mover (vibração leve); segurar ~3 s parado seleciona (segunda vibração).
export const ESPERA = 1000;      // ms segurando para pegar (mover) — REGRAS v3 (texto 29): ~1 s
export const SELECIONAR = 3000;  // ms segurando TOTALMENTE parado: modo de seleção — REGRAS v3: ~3 s
/** Depois de pegar, mexer mais que isto (1–2 mm) cancela a seleção e vale só o mover (REGRAS 0.4). */
const PARADO = 6;
/** Quanto o dedo pode andar e ainda ser "toque" ou "segurar" (alguns milímetros); mais que isso é rolagem. */
export const TOLERANCIA = 12;

// ---------- 0.69 (B1): o toque que termina um "segurar" não abre o item ----------
// Causa do bug: o Pressable do item é quem recebe o toque. Segurando parado, nenhum "mover" chega ao arraste,
// então o Pressable continua dono do toque e, ao soltar, dispara o onPress (abria a matéria) junto com a seleção.
// Quem desenha o item embrulha o "abrir" com seNaoSegurou(...): enquanto o dedo segura e logo depois de soltar, o toque é ignorado.
let segurando = false;
let liberaEm = 0;
/** false enquanto um item está sendo segurado e por 0,4 s depois de soltar. */
export const toqueLiberado = () => !segurando && Date.now() >= liberaEm;
/** Embrulha o onPress de um item de lista: não abre quando o toque foi um "segurar" (seleção ou arraste). */
export function seNaoSegurou<A extends unknown[]>(fn: (...a: A) => void) {
  return (...a: A) => { if (toqueLiberado()) fn(...a); };
}
const marcarSegurou = () => { segurando = true; };
/** Texto 29: um item está sendo segurado (pego ou selecionando): os cartões e blocos de fora não deslizam para o lado. */
export const segurandoItem = () => segurando;
const soltouDedo = () => { if (segurando) { segurando = false; liberaEm = Date.now() + 400; } };

/** Lista que se reorganiza arrastando. */
export function Ordenavel<T>(props: Props<T>) {
  const cx = useCaixas();   // "Tamanho das caixas": espaço padrão entre os itens (Grande = 8, como antes)
  const { itens, chave, render, grupo, listaId, gap = cx.gap(8), grade, alca, style, estiloItem, selecao } = props;
  const desligado = props.desligado || !!selecao?.ativa;
  const P = useRef(props); P.current = props;
  const area = useContext(AreaCtx);
  const cont = useRef<View | null>(null);
  const lay = useRef<Record<string, Rect>>({});
  const viewsItem = useRef<Record<string, View | null>>({});
  const [arr, setArr] = useState<{ k: string; de: number; para: number } | null>(null);
  const ativo = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timerSel = useRef<ReturnType<typeof setTimeout> | null>(null);   // 0.69: seleção ao segurar parado
  const selecionou = useRef(false);   // a seleção já entrou com o dedo ainda no item (se andar, vira arraste)
  const vibrouArraste = useRef(false);
  const moveuAlca = useRef(false);   // texto 28: o dedo andou no cabeçalho antes de pegar (rolagem: não recolhe)
  const concedido = useRef(false);   // texto 29: o gesto de arrastar já ficou com o dedo (PanResponder)
  const toque = useRef({ px: 0, py: 0 });
  const ini = useRef({ px: 0, py: 0, sy: 0, item: { x: 0, y: 0, w: 0, h: 0 } as Rect, cont: { x: 0, y: 0, w: 0, h: 0 } as Rect });
  const dedo = useRef({ px: 0, py: 0 });
  const zonasMed = useRef<{ chave: string; rect: Rect; lista: boolean }[]>([]);
  const vp = useRef<Rect | null>(null);
  const loop = useRef<ReturnType<typeof setInterval> | null>(null);
  const local = useRef(new Animated.ValueXY()).current;   // sem área: o próprio item se move
  const estado = useRef({ itens, para: 0 });
  estado.current.itens = itens;
  const minhaZona = grupo && listaId ? `${grupo}:${listaId}` : null;

  // a própria lista também recebe itens de outras listas do grupo
  useEffect(() => {
    if (!grupo || !listaId) return;
    const k = `l:${grupo}:${listaId}:${Math.random()}`;
    zonas.set(k, { id: listaId, grupo, medir: () => medirView(cont.current), lista: true });
    return () => { zonas.delete(k); };
  }, [grupo, listaId]);

  const limpar = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (timerSel.current) { clearTimeout(timerSel.current); timerSel.current = null; }
  };
  /** texto 29: este item pode entrar na seleção? */
  const podeSel = (k: string) => {
    if (!P.current.selecao) return false;
    const x = estado.current.itens.find(y => chave(y) === k);
    return !!x && (P.current.selecionavel?.(x) ?? true);
  };
  const longe = (px: number, py: number, lim = TOLERANCIA) => Math.hypot(px - toque.current.px, py - toque.current.py) > lim;

  const calcular = useCallback(() => {
    const k = ativo.current; if (!k) return;
    const { px, py } = dedo.current;
    const dxDedo = px - ini.current.px, dyDedo = py - ini.current.py;
    const rolou = (area?.y.current ?? 0) - ini.current.sy;
    if (area) area.mover(dxDedo, dyDedo); else local.setValue({ x: grade ? dxDedo : 0, y: dyDedo + rolou });
    // outra lista ou zona do grupo embaixo do dedo?
    let zona: string | null = null;
    if (grupo) {
      const dentro = zonasMed.current
        .map(z => ({ ...z, r: { ...z.rect, y: z.rect.y - rolou } }))
        .filter(z => px >= z.r.x && px <= z.r.x + z.r.w && py >= z.r.y && py <= z.r.y + z.r.h)
        .sort((a, b) => a.r.w * a.r.h - b.r.w * b.r.h);
      const z = dentro[0];
      if (z && z.chave !== minhaZona) zona = z.chave;
    }
    setZona(zona);
    // posição dentro da própria lista
    const its = estado.current.itens;
    const de = its.findIndex(x => chave(x) === k);
    let para = de;
    if (!zona && de >= 0) {
      const contY = ini.current.cont.y - rolou, contX = ini.current.cont.x;
      const cy = ini.current.item.y + dyDedo + ini.current.item.h / 2 - contY;
      const cx = ini.current.item.x + dxDedo + ini.current.item.w / 2 - contX;
      if (grade) {
        let melhor = Infinity;
        its.forEach((x, i) => {
          const L = lay.current[chave(x)]; if (!L) return;
          const d = Math.hypot(L.x + L.w / 2 - cx, L.y + L.h / 2 - cy);
          if (d < melhor) { melhor = d; para = i; }
        });
      } else {
        para = 0;
        its.forEach((x, i) => {
          if (i === de) return;
          const L = lay.current[chave(x)]; if (!L) return;
          if (cy > L.y + L.h / 2) para = i >= de ? i : i + 1;
        });
        para = Math.max(0, Math.min(its.length - 1, para));
      }
    }
    if (para !== estado.current.para) { estado.current.para = para; vibrar(); }
    setArr(a => (a && a.para === para ? a : a ? { ...a, para } : a));
  }, [area, chave, grade, grupo, local, minhaZona]);

  const pararLoop = () => { if (loop.current) { clearInterval(loop.current); loop.current = null; } };
  const autoRolar = () => {
    if (!area || !vp.current) return;
    const { py } = dedo.current, v = vp.current, borda = 70;
    const vel = py < v.y + borda ? -Math.ceil((v.y + borda - py) / 5) : py > v.y + v.h - borda ? Math.ceil((py - (v.y + v.h - borda)) / 5) : 0;
    if (!vel) { pararLoop(); return; }
    if (loop.current) return;
    loop.current = setInterval(() => {
      const v2 = vp.current!, py2 = dedo.current.py;
      const s = py2 < v2.y + borda ? -Math.ceil((v2.y + borda - py2) / 5) : py2 > v2.y + v2.h - borda ? Math.ceil((py2 - (v2.y + v2.h - borda)) / 5) : 0;
      if (!s || !area.rolarPor(Math.max(-24, Math.min(24, s)))) { pararLoop(); return; }
      calcular();
    }, 16);
  };

  const ativar = async (k: string) => {
    timer.current = null;
    const i = estado.current.itens.findIndex(x => chave(x) === k);
    if (i < 0) return;
    ativo.current = k;
    concedido.current = false;
    estado.current.para = i;
    marcarSegurou();
    vibrouArraste.current = false;
    // texto 28 (REGRAS 0.3): pegou (~1 s): vibração leve; a seleção (~3 s) tem a segunda vibração
    vibrar(true); vibrouArraste.current = true;
    area?.setRolagem(false);
    const [rItem, rCont, rVp] = await Promise.all([medirView(viewsItem.current[k]), medirView(cont.current), area?.medirViewport() ?? Promise.resolve(null)]);
    if (ativo.current !== k) return;
    ini.current = { px: toque.current.px, py: toque.current.py, sy: area?.y.current ?? 0, item: rItem ?? { x: 0, y: 0, w: 0, h: 0 }, cont: rCont ?? { x: 0, y: 0, w: 0, h: 0 } };
    dedo.current = { ...toque.current };
    vp.current = rVp;
    zonasMed.current = [];
    if (grupo) {
      const lista = [...zonas.values()].filter(z => z.grupo === grupo);
      const med = await Promise.all(lista.map(z => z.medir()));
      zonasMed.current = lista.map((z, j) => ({ chave: `${z.grupo}:${z.id}`, rect: med[j]!, lista: !!z.lista })).filter(z => z.rect);
    }
    if (ativo.current !== k || selecionou.current) return;   // 0.69: a seleção entrou enquanto media
    const x = estado.current.itens[i];
    if (area && rItem) area.fantasma(P.current.render(x, i, { arrastando: true, alca: {}, alvo: false }), rItem);
    // texto 29: segurando parado depois de pegar, o anelzinho mostra quanto falta para selecionar
    if (podeSel(k) && !P.current.selecao!.ativa && timerSel.current) area?.anel({ x: toque.current.px, y: toque.current.py, ms: Math.max(300, SELECIONAR - ESPERA) });
    setArr({ k, de: i, para: i });
    setArrastando(true);
    P.current.onArrastando?.(true);
  };

  /** 0.69 (B1): segurou parado ~0,5 s: entra na seleção agora, com este item marcado. O item continua "na mão":
   *  se o dedo andar antes de soltar, a seleção é desfeita e vira arraste (reordenar). */
  const selecionarJa = (k: string) => {
    timerSel.current = null;
    const sel = P.current.selecao;
    if (!sel || sel.ativa) return;
    area?.anel(null);
    if (longe(dedo.current.px, dedo.current.py, PARADO)) return;
    marcarSegurou();
    vibrarSelecao();
    if (ativo.current === k) {
      area?.fantasma(null);
      local.setValue({ x: 0, y: 0 });
      setArr(null);
      setArrastando(false);
      P.current.onArrastando?.(false);
      selecionou.current = true;
    }
    sel.onIniciar(k);
  };
  /** Depois da seleção ter entrado, o dedo andou: desfaz a seleção e levanta o item de novo (arrastar). */
  const voltarAoArraste = () => {
    const k = ativo.current; if (!k) return;
    selecionou.current = false;
    const sel = P.current.selecao;
    if (sel) { if (sel.onCancelar) sel.onCancelar(); else sel.onAlternar(k); }
    const i = estado.current.itens.findIndex(x => chave(x) === k);
    if (i < 0) return;
    estado.current.para = i;
    vibrar(true); vibrouArraste.current = true;
    area?.setRolagem(false);
    if (area) area.fantasma(P.current.render(estado.current.itens[i], i, { arrastando: true, alca: {}, alvo: false }), ini.current.item);
    setArr({ k, de: i, para: i });
    setArrastando(true);
    P.current.onArrastando?.(true);
  };

  const soltar = () => {
    soltouDedo();
    area?.anel(null);
    if (selecionou.current) {
      // seleção já entrou segurando: só larga o item, sem reordenar e sem abrir
      selecionou.current = false;
      limpar(); pararLoop();
      ativo.current = null;
      area?.setRolagem(true);
      area?.fantasma(null);
      local.setValue({ x: 0, y: 0 });
      setZona(null); setArrastando(false); setArr(null);
      return;
    }
    limpar(); pararLoop();
    const k = ativo.current;
    // segurou e soltou sem mexer: modo de seleção (0.65)
    const parado = Math.hypot(dedo.current.px - ini.current.px, dedo.current.py - ini.current.py) < 10;
    ativo.current = null;
    area?.setRolagem(true);
    area?.fantasma(null);
    local.setValue({ x: 0, y: 0 });
    // texto 28: o item foi pego em cima do texto: soltar não abre o teclado nele
    if (k && Platform.OS !== 'web') setTimeout(() => Keyboard.dismiss(), 80);
    const zona = alvoZona;
    setZona(null);
    setArrastando(false);
    P.current.onArrastando?.(false);
    setArr(null);
    if (!k) return;
    const its = estado.current.itens;
    const de = its.findIndex(x => chave(x) === k);
    if (de < 0) return;
    if (zona && P.current.onMover) {
      const destino = zona.slice((grupo ?? '').length + 1);
      P.current.onMover(its[de], destino, -1);
      vibrar(true);
      return;
    }
    const para = estado.current.para;
    // texto 28 (REGRAS 0.4): pegou (~1 s) e soltou sem mexer, antes dos 3 s: não seleciona nem abre (só larga o item)
    if (para === de && parado) return;
    if (para !== de) {
      const novos = [...its];
      const [x] = novos.splice(de, 1);
      novos.splice(para, 0, x);
      P.current.onOrdem(novos);
    }
  };

  const pan = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponderCapture: () => !!ativo.current,
    onMoveShouldSetPanResponderCapture: () => !!ativo.current,
    onPanResponderTerminationRequest: () => false,
    onPanResponderGrant: () => { concedido.current = true; },
    onPanResponderMove: e => {
      dedo.current = { px: e.nativeEvent.pageX, py: e.nativeEvent.pageY };
      const andou = longe(dedo.current.px, dedo.current.py);
      // texto 29 (REGRAS 0.4): depois de pegar, mexer 1–2 mm já cancela a seleção (vale só o mover)
      if (timerSel.current && longe(dedo.current.px, dedo.current.py, PARADO)) { clearTimeout(timerSel.current); timerSel.current = null; area?.anel(null); }
      if (selecionou.current) { if (andou) voltarAoArraste(); else return; }
      if (andou && P.current.selecao && !vibrouArraste.current) { vibrouArraste.current = true; vibrar(true); }
      calcular(); autoRolar();
    },
    onPanResponderRelease: soltar,
    onPanResponderTerminate: soltar,
  }), [calcular]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Texto 29 (item 1): CAUSA de "vibra, mas o item não sai do lugar": com o item já pego, o primeiro movimento do dedo
   *  fazia o sistema "cancelar" o toque do item (ele achava que ia rolar a tela) e o app largava o item na hora.
   *  Agora, com o item na mão, o cancelamento é ignorado: quem segue com o dedo é o gesto de arrastar. Só se o arrastar
   *  não assumir o dedo (ex.: o sistema tirou o toque de verdade) o item é largado. */
  const cancelouComItem = (k: string, largar: () => void) => {
    setTimeout(() => { if (ativo.current === k && !concedido.current) largar(); }, 350);
  };

  // toque longo: começa no toque, cancela se o dedo andar antes (é rolagem)
  const tocar = (k: string) => {
    // texto 19 (H1): alça só de arrastar. CAUSA do "⠿ não arrasta": a alça só levantava o bloco depois de o dedo
    // ficar ~0,3 s PARADO; nesse tempo a rolagem da janela continuava ligada e, ao primeiro movimento, ela ficava
    // com o dedo (a janela rolava e o bloco não saía do lugar). Agora a rolagem trava no toque e o bloco levanta logo.
    // Texto 28 (A, REGRAS 3.2/3.3 e 0): cabeçalho do bloco. Antes a rolagem travava no toque e o bloco levantava com
    // 6 px de movimento (rolar passando o dedo num cabeçalho pegava o bloco). Agora: tocar recolhe/abre; passar o dedo
    // rola; segurar ~1 s pega o bloco para mover.
    if (alca && P.current.alcaImediata) {
      const comecarJa = (px: number, py: number, e: any) => {
        if (ativo.current || desligado) return;
        e?.stopPropagation?.();
        toque.current = { px, py }; dedo.current = { px, py };
        limpar(); selecionou.current = false; moveuAlca.current = false;
        timer.current = setTimeout(() => ativar(k), ESPERA);
        // texto 29: segurar ~3 s parado também seleciona os blocos (Texto livre, Desenho)
        if (podeSel(k)) timerSel.current = setTimeout(() => selecionarJa(k), SELECIONAR);
      };
      const andarJa = (px: number, py: number) => {
        if (!ativo.current) dedo.current = { px, py };
        if (!ativo.current && longe(px, py)) { moveuAlca.current = true; limpar(); }   // é rolagem
      };
      const fimJa = () => {
        if (!ativo.current) {
          const tocou = !!timer.current && !moveuAlca.current;
          limpar();
          if (tocou) P.current.onToqueAlca?.(k);
          return;
        }
        limpar(); soltouDedo(); if (ativo.current === k) setTimeout(() => { if (ativo.current === k) soltar(); }, 0);
      };
      const cancelarJa = () => { if (!ativo.current) { limpar(); return; } cancelouComItem(k, fimJa); };
      if (Platform.OS === 'web') return {
        onPointerDown: (e: any) => comecarJa(e.nativeEvent.pageX ?? e.nativeEvent.clientX, e.nativeEvent.pageY ?? e.nativeEvent.clientY, e),
        onPointerMove: (e: any) => andarJa(e.nativeEvent.pageX ?? e.nativeEvent.clientX, e.nativeEvent.pageY ?? e.nativeEvent.clientY),
        onPointerUp: fimJa, onPointerCancel: cancelarJa,
      };
      return {
        onTouchStart: (e: any) => comecarJa(e.nativeEvent.pageX, e.nativeEvent.pageY, e),
        onTouchMove: (e: any) => andarJa(e.nativeEvent.pageX, e.nativeEvent.pageY),
        onTouchEnd: fimJa, onTouchCancel: cancelarJa,
      };
    }
    const comecar = (px: number, py: number, e: any) => {
      if (ativo.current) return;
      // em seleção, o toque é da caixinha do item: a lista de fora (ex.: o fichário) não levanta
      if (P.current.selecao?.ativa) { e?.stopPropagation?.(); return; }
      dedo.current = { px, py };
      if (desligado) {
        // lista sem reordenar (por data): segurar ainda entra no modo de seleção (0.65); 0.69: na hora, aos 0,5 s
        if (!podeSel(k)) return;
        toque.current = { px, py }; limpar();
        timerSel.current = setTimeout(() => { timerSel.current = null; marcarSegurou(); vibrarSelecao(); P.current.selecao?.onIniciar(k); }, SELECIONAR);
        return;
      }
      e?.stopPropagation?.();
      toque.current = { px, py };
      limpar();
      selecionou.current = false;
      timer.current = setTimeout(() => ativar(k), ESPERA);
      if (podeSel(k)) timerSel.current = setTimeout(() => selecionarJa(k), SELECIONAR);
    };
    const andar = (px: number, py: number) => {
      if (!ativo.current) dedo.current = { px, py };
      if ((timer.current || timerSel.current) && !ativo.current && longe(px, py)) limpar();   // é rolagem
    };
    const fim = () => { limpar(); soltouDedo(); if (ativo.current === k) setTimeout(() => { if (ativo.current === k) soltar(); }, 0); };
    const cancelar = () => { if (ativo.current === k) cancelouComItem(k, fim); else fim(); };
    if (Platform.OS === 'web') return {
      onPointerDown: (e: any) => comecar(e.nativeEvent.pageX ?? e.nativeEvent.clientX, e.nativeEvent.pageY ?? e.nativeEvent.clientY, e),
      onPointerMove: (e: any) => andar(e.nativeEvent.pageX ?? e.nativeEvent.clientX, e.nativeEvent.pageY ?? e.nativeEvent.clientY),
      onPointerUp: fim, onPointerCancel: cancelar,
    };
    return {
      onTouchStart: (e: any) => comecar(e.nativeEvent.pageX, e.nativeEvent.pageY, e),
      onTouchMove: (e: any) => andar(e.nativeEvent.pageX, e.nativeEvent.pageY),
      onTouchEnd: fim, onTouchCancel: cancelar,
    };
  };

  /** Grade (ex.: cômodos da Casa): os outros cartões deslizam para as casas vizinhas abrindo o espaço, como nas listas (0.47). */
  const deslocGrade = (i: number, k: string) => {
    if (!arr || !grade || alvoZona || k === arr.k) return null;
    let ni = i;
    if (arr.de < arr.para && i > arr.de && i <= arr.para) ni = i - 1;
    else if (arr.para < arr.de && i >= arr.para && i < arr.de) ni = i + 1;
    if (ni === i) return null;
    const A = lay.current[k], B = lay.current[chave(itens[ni])];
    if (!A || !B) return null;
    return [{ translateX: B.x - A.x }, { translateY: B.y - A.y }];
  };
  const deslocamento = (i: number, k: string) => {
    if (!arr || grade || alvoZona) return 0;
    const L = lay.current[arr.k]; if (!L || k === arr.k) return 0;
    const passo = L.h + gap;
    if (arr.de < arr.para && i > arr.de && i <= arr.para) return -passo;
    if (arr.para < arr.de && i >= arr.para && i < arr.de) return passo;
    return 0;
  };
  const zonaAlvo = useZonaAlvo();
  const recebe = !!minhaZona && zonaAlvo === minhaZona;

  return (
    <View ref={cont} collapsable={false} style={[grade ? { flexDirection: 'row', flexWrap: 'wrap', gap } : { gap }, recebe ? { backgroundColor: 'rgba(34,197,94,0.10)', borderRadius: 12 } : null, style]}>
      {itens.map((x, i) => {
        const k = chave(x);
        const eu = arr?.k === k;
        const handlers = tocar(k);
        const alvo = !!arr && !!grade && !alvoZona && arr.para === i && !eu;
        const dg = deslocGrade(i, k);
        return (
          <Animated.View key={k} ref={(r: any) => { viewsItem.current[k] = r; }} collapsable={false}
            onLayout={e => { const l = e.nativeEvent.layout; lay.current[k] = { x: l.x, y: l.y, w: l.width, h: l.height }; }}
            {...pan.panHandlers} {...(alca ? {} : handlers)}
            style={[estiloItem?.(x, i), eu && area ? { opacity: 0.25 } : null,
              eu && !area ? { zIndex: 50, elevation: 8, transform: [{ translateX: local.x }, { translateY: local.y }, { scale: 1.03 }] } : null,
              !eu && arr && !grade ? { transform: [{ translateY: deslocamento(i, k) }] } : null,
              dg ? { transform: dg } : null,
            ] as any}>
            {render(x, i, { arrastando: eu, alca: handlers, alvo })}
            {!!selecao?.ativa && (props.selecionavel?.(x) ?? true) && (
              // texto 29: o dedo que acabou de segurar (e entrou na seleção) não marca, ao soltar, o item vizinho
              // (a barra "N selecionados" aparece em cima e empurra a lista: o dedo fica sobre o item de cima)
              <Pressable onPress={seNaoSegurou(() => selecao.onAlternar(k))} accessibilityRole="checkbox" accessibilityState={{ checked: selecao.marcados.has(k) }}
                style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, borderRadius: 14, backgroundColor: selecao.marcados.has(k) ? 'rgba(99,102,241,0.18)' : 'rgba(0,0,0,0.02)', justifyContent: 'center', paddingLeft: 6 }}>
                <View style={{ width: 24, height: 24, borderRadius: 12, borderWidth: 2, borderColor: selecao.cor ?? '#6366F1', backgroundColor: selecao.marcados.has(k) ? selecao.cor ?? '#6366F1' : C.card, alignItems: 'center', justifyContent: 'center' }}>
                  {selecao.marcados.has(k) && <Text style={{ color: '#fff', fontWeight: '900', fontSize: 13 }}>✓</Text>}
                </View>
              </Pressable>
            )}
          </Animated.View>
        );
      })}
    </View>
  );
}

/** Texto 26 (B, REGRAS 1.3 e 2.2): sem pontinhos ⠿. O item inteiro é a "alça" (segurar numa parte livre e arrastar);
 *  ponha isto nos campos de texto (e botões) dentro do item: segurar neles escreve / seleciona, não levanta o item. */
const pararToque = (e: any) => e?.stopPropagation?.();
export const semArrastar: object = Platform.OS === 'web' ? { onPointerDown: pararToque } : { onTouchStart: pararToque };

/** Reordena só um pedaço de uma lista maior (ex.: as caixas desta aba dentro de todas as caixas), mantendo os outros no lugar. */
export function reordenarParte<T>(todos: T[], parteNova: T[], chave: (x: T) => string): T[] {
  const ids = new Set(parteNova.map(chave));
  const fila = [...parteNova];
  return todos.map(x => (ids.has(chave(x)) ? fila.shift()! : x));
}
